import { CatalogItemType } from '@common/model/catalog/item/type';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { vacancyApi } from '@cp/entities/vacancy';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UXSlice, needHelpShownForGuestSelector, needHelpShownForRegisteredUserSelector } from 'src/shared/ux';
import { useIsAnyModalOpen } from 'src/shared/modalProvider';
import { useClientSelectionService, usePrefilter } from 'src/features/clientSelection';
import { ClientSelection } from 'src/entities/client';
import { useAuth } from 'src/features/auth';

export const useNeedHelpTimingModalTrigger = (clientSelection: ClientSelection) => {
  const isLoggedIn = useIsLoggedIn();
  const isAnyModalOpened = useIsAnyModalOpen();
  const [loadClientVacancies] = vacancyApi.endpoints.getVacancyListForCurrectClient.useLazyQuery();
  const dispatch = useDispatch();
  const isNeedHelpShownForGuest = useSelector(needHelpShownForGuestSelector);
  const isNeedHelpShownForRegisteredUser = useSelector(needHelpShownForRegisteredUserSelector);
  const runPrefilter = usePrefilter();
  const auth = useAuth();
  const selectionService = useClientSelectionService();

  React.useEffect(() => {
    const countdown = isLoggedIn ? 40000 : 25000;
    const { urgencyScore } = selectionService.getSelections();
    const shown = typeof urgencyScore !== 'undefined' || (isLoggedIn ? isNeedHelpShownForRegisteredUser : isNeedHelpShownForGuest);
    if (shown) {
      return;
    }

    const guestFlow = async () => {
      if (clientSelection.catalogType !== CatalogItemType.Resume) {
        return;
      }
      dispatch(UXSlice.actions.setNeedHelpShownForGuest(true));
      const cs = await runPrefilter({ omitQuestions: Object.keys(clientSelection), requiredQuestions: ['urgencyScore'] });
      await cs.mapAsync((selections) => (selections.urgencyScore ? auth(selections) : Promise.resolve(null)));
    };

    const clientFlow = async () => {
      if (clientSelection.catalogType !== CatalogItemType.Resume) {
        return;
      }
      const { data: clientVacanciesList } = await loadClientVacancies();
      if (clientVacanciesList?.length) {
        return;
      }
      dispatch(UXSlice.actions.setNeedHelpShownForRegisteredUser(true));
      return;
    };
    const flow: () => Promise<unknown> = isLoggedIn ? clientFlow : guestFlow;

    const timer = setTimeout(() => {
      if (!isAnyModalOpened) {
        console.debug('Start prefilter by timeout trigger');
        return flow();
      }
    }, countdown);

    return () => {
      clearTimeout(timer);
    };
  }, [
    clientSelection,
    selectionService,
    auth,
    isLoggedIn,
    isAnyModalOpened,
    runPrefilter,
    loadClientVacancies,
    isNeedHelpShownForRegisteredUser,
    isNeedHelpShownForGuest,
    dispatch,
  ]);
};
